/** Converte HH:mm em minutos. */
export function printTimeToMinutes(time) {
    if (!time || typeof time !== 'string') {
        return 0;
    }
    const [hoursPart, minutesPart] = time.split(':');
    const hours = parseInt(hoursPart, 10) || 0;
    const minutes = parseInt(minutesPart, 10) || 0;
    return hours * 60 + minutes;
}

export function createCalculatorPart(overrides = {}) {
    return {
        id: overrides.id || `part-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: overrides.name || '',
        time: overrides.time || '01:00',
        grams: overrides.grams ?? 100,
    };
}

export function sumParts(parts) {
    return parts.reduce(
        (acc, part) => ({
            minutes: acc.minutes + printTimeToMinutes(part.time),
            grams: acc.grams + (parseFloat(part.grams) || 0),
        }),
        { minutes: 0, grams: 0 },
    );
}

function parseNumber(value, fallback = 0) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
}

/** Repassa preço para cobrir taxa percentual sobre o valor final (ex.: marketplace 16%). */
export function grossUpForPercentFee(amount, percent) {
    const fee = parseNumber(percent, 0);
    if (fee <= 0 || fee >= 100) {
        return amount;
    }
    return amount / (1 - fee / 100);
}

/** Valor líquido após descontar taxa percentual do preço bruto. */
export function netAfterPercentFee(grossAmount, percent) {
    const fee = parseNumber(percent, 0);
    return grossAmount * (1 - fee / 100);
}

/**
 * Depreciação proporcional ao tempo de impressão.
 * Ex.: R$ 5.000 / 5.000 h = R$ 1/h; 2 h de job → R$ 2 de depreciação.
 */
export function calculateDepreciation(printerValue, usefulLifeHours, totalMinutes) {
    const value = parseNumber(printerValue, 0);
    const lifeHours = parseNumber(usefulLifeHours, 0);
    if (value <= 0 || lifeHours <= 0 || totalMinutes <= 0) {
        return { depreciation: 0, costPerHour: 0, printHours: 0 };
    }
    const costPerHour = value / lifeHours;
    const printHours = totalMinutes / 60;
    const depreciation = costPerHour * printHours;
    return { depreciation, costPerHour, printHours };
}

/**
 * @param {object} input
 * @returns {object} breakdown
 */
export function calculatePriceQuote(input) {
    const consumptionW = parseNumber(input.consumption, 0);
    const energyTariff = parseNumber(input.energyCost, 0);
    const costPerKg = parseNumber(input.costPerKilogram, 0);
    const packaging = parseNumber(input.packagingCost, 0);
    const other = parseNumber(input.otherCosts, 0);
    const markupPercent = parseNumber(input.markup, 0);
    const marketplacePercent = parseNumber(input.marketplacePercent, 0);
    const cardFeePercent = parseNumber(input.cardFeePercent, 0);
    const pjTaxPercent = parseNumber(input.pjTaxPercent, 0);
    const reworkPercent = parseNumber(input.reworkPercent, 0);
    const salePrice = parseNumber(input.salePrice, 0);

    let totalMinutes = 0;
    let totalGrams = 0;

    if (input.multipartEnabled && Array.isArray(input.parts) && input.parts.length > 0) {
        const totals = sumParts(input.parts);
        totalMinutes = totals.minutes;
        totalGrams = totals.grams;
    } else {
        totalMinutes = printTimeToMinutes(input.printTime);
        totalGrams = parseNumber(input.usedMaterial, 0);
    }

    const kw = consumptionW / 1000;
    const kwh = (kw / 60) * totalMinutes;
    const energy = kwh * energyTariff;
    const material = (costPerKg / 1000) * totalGrams;
    const { depreciation, costPerHour, printHours } = calculateDepreciation(
        input.printerValue,
        input.printerUsefulLifeHours,
        totalMinutes,
    );
    const productionCostBeforeRework = energy + material + packaging + other + depreciation;
    const reworkReserve =
        reworkPercent > 0 ? productionCostBeforeRework * (reworkPercent / 100) : 0;
    const productionCost = productionCostBeforeRework + reworkReserve;

    const priceWithMarkup = productionCost * (1 + markupPercent / 100);
    const afterMarketplace = grossUpForPercentFee(priceWithMarkup, marketplacePercent);
    const afterCard = grossUpForPercentFee(afterMarketplace, cardFeePercent);
    const suggestedListPrice = grossUpForPercentFee(afterCard, pjTaxPercent);

    const marketplaceFeeOnList = suggestedListPrice * (marketplacePercent / 100);
    const cardFeeOnList = suggestedListPrice * (cardFeePercent / 100);
    const pjTaxFeeOnList = suggestedListPrice * (pjTaxPercent / 100);

    let netFromSale = salePrice;
    netFromSale = netAfterPercentFee(netFromSale, marketplacePercent);
    netFromSale = netAfterPercentFee(netFromSale, cardFeePercent);
    netFromSale = netAfterPercentFee(netFromSale, pjTaxPercent);
    const profitOnMarkup = priceWithMarkup - productionCost;
    const marginOnMarkup = priceWithMarkup > 0 ? (profitOnMarkup / priceWithMarkup) * 100 : 0;

    const profitOnSale = netFromSale - productionCost;
    const marginOnSale = salePrice > 0 ? (profitOnSale / salePrice) * 100 : 0;
    const totalFeesOnInformedPrice = salePrice > 0 ? salePrice - netFromSale : 0;

    let netFromSuggested = suggestedListPrice;
    netFromSuggested = netAfterPercentFee(netFromSuggested, marketplacePercent);
    netFromSuggested = netAfterPercentFee(netFromSuggested, cardFeePercent);
    netFromSuggested = netAfterPercentFee(netFromSuggested, pjTaxPercent);
    const profitOnSuggested = netFromSuggested - productionCost;
    const marginOnSuggested =
        suggestedListPrice > 0 ? (profitOnSuggested / suggestedListPrice) * 100 : 0;

    return {
        totalMinutes,
        totalGrams,
        energy,
        material,
        packaging,
        other,
        depreciation,
        costPerHour,
        printHours,
        productionCostBeforeRework,
        reworkReserve,
        productionCost,
        priceWithMarkup,
        profitOnMarkup,
        marginOnMarkup,
        suggestedListPrice,
        marketplaceFeeOnList,
        cardFeeOnList,
        pjTaxFeeOnList,
        salePrice,
        totalFeesOnInformedPrice,
        netFromSale,
        profitOnSuggested,
        marginOnSuggested,
        profitOnSale,
        marginOnSale,
    };
}

export const CALCULATOR_STORAGE_KEYS = {
    productName: 'calculatorProductName',
    consumption: 'printerConsumption',
    energyCost: 'energyCost',
    costPerKilogram: 'costPerKilogram',
    markup: 'markup',
    packagingCost: 'calculatorPackagingCost',
    marketplacePercent: 'calculatorMarketplacePercent',
    cardFeePercent: 'calculatorCardFeePercent',
    pjTaxPercent: 'calculatorPjTaxPercent',
    multipartEnabled: 'calculatorMultipartEnabled',
    parts: 'calculatorParts',
    printerValue: 'calculatorPrinterValue',
    printerUsefulLifeHours: 'calculatorPrinterUsefulLifeHours',
    reworkPercent: 'calculatorReworkPercent',
};

export function loadCalculatorPreference(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null || raw === '') {
            return fallback;
        }
        return raw;
    } catch {
        return fallback;
    }
}

export function saveCalculatorPreference(key, value) {
    try {
        localStorage.setItem(key, String(value));
    } catch {
        /* ignore */
    }
}

export function loadCalculatorParts() {
    try {
        const raw = localStorage.getItem(CALCULATOR_STORAGE_KEYS.parts);
        if (!raw) {
            return [createCalculatorPart({ name: 'Peça 1' })];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [createCalculatorPart({ name: 'Peça 1' })];
        }
        return parsed.map((p) => createCalculatorPart(p));
    } catch {
        return [createCalculatorPart({ name: 'Peça 1' })];
    }
}

export function saveCalculatorParts(parts) {
    try {
        localStorage.setItem(CALCULATOR_STORAGE_KEYS.parts, JSON.stringify(parts));
    } catch {
        /* ignore */
    }
}

export function getDefaultCalculatorInputs() {
    return {
        productName: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.productName, ''),
        multipartEnabled: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.multipartEnabled, 'false') === 'true',
        parts: loadCalculatorParts(),
        printTime: '01:00',
        consumption: parseInt(loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.consumption, '350'), 10) || 350,
        printerValue: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.printerValue, '5000'),
        printerUsefulLifeHours: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.printerUsefulLifeHours, '5000'),
        energyCost: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.energyCost, '0.98'),
        costPerKilogram: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.costPerKilogram, '99.90'),
        usedMaterial: 100,
        packagingCost: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.packagingCost, '0'),
        otherCosts: 0,
        reworkPercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.reworkPercent, '10'),
        marketplacePercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.marketplacePercent, '0'),
        cardFeePercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.cardFeePercent, '0'),
        pjTaxPercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.pjTaxPercent, '0'),
        markup: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.markup, '100'),
        salePrice: 39.9,
    };
}
